import React, { useCallback } from 'react';
import { Button } from 'antd';
import { useAssetMVPermissions, useTypedSelector } from '@hooks';
import DropdownDecisions from '@components/MediaViewerHeader/components/DropdownDecisions';
import type { DropdownDecisionsType } from '@components/MediaViewerHeader/components/DropdownDecisions/DropdownDecisions';

import { ReactComponent as DownloadSvg } from '@assets/icons/MediaViewer/download.svg';
import { useAuth } from '@hooks/useAuth';
import { LgWidth } from '@components/Responsive';
import { useLocation } from 'react-router-dom';

import ApprovalWorkflowButton from '@components/MediaViewerHeader/components/ApprovalWorkflowButton';
import { useResponsive } from '@hooks/useResponsive';
import { download } from '@helpers/download';

type HeaderReviewerActionsProps = {
  approvalWorkflowId?: string;
  showApproval?: boolean;
  showDropdownDecisions?: boolean;
  dropdownDecisionsType?: DropdownDecisionsType;
  password?: string | null;
};

function HeaderReviewerActions({
  approvalWorkflowId,
  showApproval = true,
  showDropdownDecisions = true,
  dropdownDecisionsType = 'asset-decisions',
  password
}: HeaderReviewerActionsProps) {
  const { isMdWidth } = useResponsive();
  const location = useLocation();
  const isSharedLink = useCallback(
    () => location.pathname.includes('external-reviewer'),
    [location]
  )();
  const canDownload = useAssetMVPermissions('download');
  const { user, logout, login } = useAuth();
  const { selectedVersion, assets: asset } = useTypedSelector(
    ({ mediaViewer }) => mediaViewer
  );

  const onSignInOrSignOut = () => {
    if (user) {
      logout({ openUrl: false });
      return;
    }

    const { pathname, search } = window.location;
    login({
      authorizationParams: { prompt: 'login' },
      appState: { returnTo: `${pathname}${search}` }
    });
  };

  const isLastVersion =
    selectedVersion?.versionNumber === asset?.asset?.versionsCount;

  const showApprovalButton =
    asset &&
    selectedVersion &&
    !isMdWidth &&
    !isSharedLink &&
    (asset.asset?.approvalWorkflow?.permissions.manage ||
      asset.asset?.approvalWorkflow?.permissions.read);

  return (
    <div className="media_viewer_actions">
      <LgWidth>
        {canDownload && !!selectedVersion?.sourceUrl && (
          <Button
            className="media_viewer_btn b-only-icon"
            onClick={() => download(selectedVersion.sourceUrl)}
          >
            <DownloadSvg />
          </Button>
        )}
      </LgWidth>
      {showApprovalButton && showApproval && (
        <ApprovalWorkflowButton
          asset={asset}
          isLastVersion={isLastVersion}
          selectedVersion={selectedVersion}
        />
      )}
      {showDropdownDecisions && (
        <DropdownDecisions
          type={dropdownDecisionsType}
          password={password}
          approvalWorkflowId={approvalWorkflowId}
        />
      )}
      <Button
        className="media_viewer_btn"
        onClick={onSignInOrSignOut}
        style={{ minWidth: 108, marginRight: 0 }}
      >
        {user ? 'Sign out' : 'Sign in'}
      </Button>
    </div>
  );
}

export default HeaderReviewerActions;
